import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Paper,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  RestoreFromTrash as ReactivateIcon,
  Block as DeactivateIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';

export default function ActiveUsersTab() {
  const theme = useTheme();
  const { canEditPage, canAccessPage, hasPermission, companyId, userRole, hierarchyLevel } = usePermissions();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [companyOwner, setCompanyOwner] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editUserForm, setEditUserForm] = useState({ id: '', role_id: '', status: '' });
  const [roles, setRoles] = useState([]);
  const [confirmCompleteRemovalDialogOpen, setConfirmCompleteRemovalDialogOpen] = useState(false);
  const [userToCompletelyRemove, setUserToCompletelyRemove] = useState(null);
  const [roleSelectionDialogOpen, setRoleSelectionDialogOpen] = useState(false);
  const [roleSelectionSearchTerm, setRoleSelectionSearchTerm] = useState('');
  const [roleSelectionHierarchyFilter, setRoleSelectionHierarchyFilter] = useState('');
  const [selectedRoleForDialog, setSelectedRoleForDialog] = useState(null);

  useEffect(() => {
    if (companyId) {
      loadUsers();
      loadCompanyOwner();
      getCurrentUser();
      loadRoles();
    }
  }, [companyId]);

  const getCurrentUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      setCurrentUserId(user?.id);
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const loadCompanyOwner = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('created_by')
        .eq('id', companyId)
        .single();
      
      if (error) throw error;
      setCompanyOwner(data?.created_by);
    } catch (error) {
      console.error('Error loading company owner:', error);
    }
  };

  const isCompanyOwner = (user) => {
    return user && companyOwner && user.user_id === companyOwner;
  };

  const isCurrentUser = (user) => {
    return user && currentUserId && user.user_id === currentUserId;
  };

  const canEditUser = (user) => {
    if (!user || !user.roles) return false;
    if (isCompanyOwner(user)) return false;
    if (isCurrentUser(user)) return false;
    return canEditPage('active_users') && hierarchyLevel < user.roles.hierarchy_level;
  };

  const canDeactivateUser = (user) => {
    if (!user || !user.roles) return false;
    if (isCompanyOwner(user)) return false;
    if (isCurrentUser(user)) return false;
    
    // Must have deactivate permission
    if (!hasPermission('settings.user_management.active_users.deactivate', 'write')) return false;
    
    // Super Admin can deactivate anyone
    if (userRole === 'super_admin') return true;
    
    // Others can only deactivate users below them in hierarchy
    return hierarchyLevel < user.roles.hierarchy_level;
  };

  const canCompletelyRemoveUser = (user) => {
    if (!user || !user.roles) return false;
    if (isCompanyOwner(user)) return false;
    if (isCurrentUser(user)) return false;
    return hasPermission('settings.user_management.active_users.complete_removal', 'delete') && hierarchyLevel < user.roles.hierarchy_level;
  };

  const canReactivateUser = (user) => {
    if (!user || !user.roles) return false;
    if (isCompanyOwner(user)) return false;
    if (isCurrentUser(user)) return false;
    
    // Must have deactivate permission
    if (!hasPermission('settings.user_management.active_users.deactivate', 'write')) return false;
    
    // Super Admin can reactivate anyone
    if (userRole === 'super_admin') return true;
    
    // Others can only reactivate users below them in hierarchy
    return hierarchyLevel < user.roles.hierarchy_level;
  };

  const loadRoles = async () => {
    try {
      const { data: rolesData, error } = await supabase
        .from('roles')
        .select('*')
        .or(`company_id.eq.${companyId},is_system_role.eq.true`)
        .order('hierarchy_level', { ascending: true });

      if (error) throw error;
      setRoles(rolesData || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const handleUpdateUser = async () => {
    try {
      if (selectedUser && (isCurrentUser(selectedUser) || isCompanyOwner(selectedUser))) {
        return;
      }

      const { error } = await supabase
        .from('company_users')
        .update({
          role_id: editUserForm.role_id,
          status: editUserForm.status
        })
        .eq('id', editUserForm.id);

      if (error) throw error;

      setEditUserDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleCompleteRemovalClick = (user) => {
    setUserToCompletelyRemove(user);
    setConfirmCompleteRemovalDialogOpen(true);
    setUserMenuAnchor(null);
  };

  const handleConfirmCompleteRemoval = async () => {
    if (!userToCompletelyRemove) return;

    try {
      if (isCurrentUser(userToCompletelyRemove) || isCompanyOwner(userToCompletelyRemove)) {
        return;
      }

      const { error } = await supabase
        .from('company_users')
        .delete()
        .eq('id', userToCompletelyRemove.id);

      if (error) throw error;

      const { error: allowedUsersError } = await supabase
        .rpc('remove_user_from_company', {
          p_company_id: companyId,
          p_user_id: userToCompletelyRemove.user_id
        });

      if (allowedUsersError) {
        console.error('Error removing from allowed_users:', allowedUsersError);
      }

      setConfirmCompleteRemovalDialogOpen(false);
      setUserToCompletelyRemove(null);
      await loadUsers();
    } catch (error) {
      console.error('Error completely removing user:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const { data: usersData, error } = await supabase
        .from('company_users')
        .select(`
          id,
          user_id,
          status,
          invited_by,
          joined_at,
          last_login,
          created_at,
          roles!inner (
            id,
            name,
            display_name,
            hierarchy_level,
            can_access_settings
          )
        `)
        .eq('company_id', companyId)
        .in('status', ['active', 'inactive'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const { data: userEmails, error: emailError } = await supabase
        .rpc('get_user_emails_for_company', { p_company_id: companyId });
      
      if (emailError) {
        console.error('Error loading user emails:', emailError);
      }
      
      const emailMap = {};
      const lastLoginMap = {};
      (userEmails || []).forEach(item => {
        emailMap[item.user_id] = item.email;
        lastLoginMap[item.user_id] = item.last_sign_in_at;
      });
      
      const enhancedUsers = [];
      
      for (const user of usersData || []) {
        try {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('first_name, last_name, full_name, phone')
            .eq('id', user.user_id)
            .single();
          
          if (profileError) {
            console.error('Profile error for user', user.user_id, ':', profileError);
          }
          
          let avatarUrl = null;
          try {
            const { data: avatarFiles } = await supabase.storage
              .from('profile-avatars')
              .list(`${user.user_id}/`, {
                limit: 1,
                sortBy: { column: 'created_at', order: 'desc' }
              });
            
            if (avatarFiles && avatarFiles.length > 0) {
              const fileName = `${user.user_id}/${avatarFiles[0].name}`;
              const { data: signedUrlData } = await supabase.storage
                .from('profile-avatars')
                .createSignedUrl(fileName, 3600);
              avatarUrl = signedUrlData?.signedUrl;
            }
          } catch (avatarError) {
            console.error('Avatar error for user', user.user_id, ':', avatarError);
          }
          
          const userEmail = emailMap[user.user_id] || 'Email not available';
          
          enhancedUsers.push({
            ...user,
            profile: profile || { first_name: '', last_name: '', full_name: '', phone: '' },
            email: userEmail,
            avatarUrl,
            auth_last_sign_in_at: lastLoginMap[user.user_id]
          });
        } catch (err) {
          console.error('Error loading user data for', user.user_id, ':', err);
          enhancedUsers.push({
            ...user,
            profile: { first_name: '', last_name: '', full_name: '', phone: '' },
            email: 'Email not available',
            avatarUrl: null,
            auth_last_sign_in_at: null
          });
        }
      }
      
      setUsers(enhancedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleStatusColor = (hierarchyLevel) => {
    switch (hierarchyLevel) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'info';
      case 4: return 'success';
      default: return 'default';
    }
  };

  const getUserStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openRoleSelectionDialog = () => {
    setRoleSelectionDialogOpen(true);
    setRoleSelectionSearchTerm('');
    setRoleSelectionHierarchyFilter('');
    setSelectedRoleForDialog(null);
  };

  const closeRoleSelectionDialog = () => {
    setRoleSelectionDialogOpen(false);
    setRoleSelectionSearchTerm('');
    setRoleSelectionHierarchyFilter('');
    setSelectedRoleForDialog(null);
  };

  const handleRoleSelection = (role) => {
    setSelectedRoleForDialog(role);
  };

  const confirmRoleSelection = () => {
    if (selectedRoleForDialog) {
      setEditUserForm({ ...editUserForm, role_id: selectedRoleForDialog.id });
    }
    closeRoleSelectionDialog();
  };

  const getFilteredRolesForSelection = () => {
    let filteredRoles = roles.filter(role => {
      if (role.hierarchy_level === 1) {
        return false;
      }
      
      return hierarchyLevel < role.hierarchy_level || userRole === 'super_admin';
    });

    if (roleSelectionHierarchyFilter) {
      filteredRoles = filteredRoles.filter(role => 
        role.hierarchy_level.toString() === roleSelectionHierarchyFilter
      );
    }

    if (roleSelectionSearchTerm && roleSelectionSearchTerm.trim()) {
      const searchTerm = roleSelectionSearchTerm.toLowerCase().trim();
      filteredRoles = filteredRoles.filter(role => {
        const name = (role.name || '').toLowerCase();
        const displayName = (role.display_name || '').toLowerCase();
        const description = (role.description || '').toLowerCase();
        
        return name.includes(searchTerm) || 
               displayName.includes(searchTerm) || 
               description.includes(searchTerm);
      });
    }

    const grouped = {};
    filteredRoles.forEach(role => {
      if (!grouped[role.hierarchy_level]) {
        grouped[role.hierarchy_level] = [];
      }
      grouped[role.hierarchy_level].push(role);
    });

    return grouped;
  };

  const getHierarchyLevelName = (level) => {
    switch (level) {
      case 1: return 'Super Administrator Level';
      default: return `Level ${level}`;
    }
  };



  if (!canAccessPage('active_users')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <PersonIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            User Management Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to view active users. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Paper sx={{ 
        borderRadius: 3, 
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  User
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Role
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Status
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Joined
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3
                  }}
                >
                  Last Login
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    bgcolor: 'white',
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    color: 'primary.main',
                    py: 2,
                    px: 3,
                    width: 120
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? users.sort((a, b) => a.roles.hierarchy_level - b.roles.hierarchy_level).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : users.sort((a, b) => a.roles.hierarchy_level - b.roles.hierarchy_level)
            ).map((user, index) => (
              <TableRow 
                key={user.id}
                hover
                sx={{ 
                  cursor: 'default',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2.5,
                    px: 3
                  }
                }}
              >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      bgcolor: theme.palette.grey[100],
                      color: 'text.primary',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                    src={user.avatarUrl || ""}
                  >
                    {user.profile?.full_name ? 
                      user.profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase() :
                      user.email?.charAt(0).toUpperCase() || 'U'
                    }
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 500 }}>
                      {user.profile?.full_name || user.profile?.first_name || user.email?.split('@')[0] || 'User'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {user.email || 'No email'}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={user.roles.display_name === 'Super Admin' ? 'Super Administrator' : user.roles.display_name}
                  color={getRoleStatusColor(user.roles.hierarchy_level)}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                  color={getUserStatusColor(user.status)}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </TableCell>
              <TableCell>
                {user.joined_at ? formatDate(user.joined_at) : 'Not joined'}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {user.auth_last_sign_in_at ? formatDate(user.auth_last_sign_in_at) : 'Never logged in'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                {(canEditUser(user) || (canDeactivateUser(user) && user.status === 'active') || (canReactivateUser(user) && user.status === 'inactive') || canCompletelyRemoveUser(user)) && (
                  <IconButton
                    onClick={(e) => {
                      setUserMenuAnchor(e.currentTarget);
                      setSelectedUser(user);
                    }}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.15)
                      }
                    }}
                  >
                    <MoreVertIcon sx={{ color: 'primary.main' }} />
                  </IconButton>
                )}
                {isCompanyOwner(user) && (
                  <Chip 
                    label="Owner" 
                    size="small" 
                    sx={{ 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)',
                      color: 'text.secondary',
                      fontWeight: 500,
                      borderRadius: 3
                    }} 
                  />
                )}
                {isCurrentUser(user) && !isCompanyOwner(user) && (
                  <Chip 
                    label="You" 
                    size="small" 
                    sx={{ 
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: 'info.main',
                      fontWeight: 500,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                    }} 
                  />
                )}
              </TableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
        borderRadius: '0 0 12px 12px'
      }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ 
            px: 3,
            py: 2,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.9rem',
              fontWeight: 500,
              color: theme.palette.text.primary
            },
            '& .MuiTablePagination-select': {
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              borderRadius: 2,
              px: 2,
              py: 1,
              fontWeight: 500,
              fontSize: '0.9rem',
              background: 'white',
              '&:hover': {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
              }
            },
            '& .MuiTablePagination-actions': {
              '& .MuiIconButton-root': {
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                borderRadius: 2,
                margin: '0 2px',
                background: 'white',
                color: theme.palette.primary.main,
                fontWeight: 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: theme.palette.primary.main,
                  color: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                },
                '&.Mui-disabled': {
                  background: alpha(theme.palette.grey[300], 0.5),
                  color: theme.palette.grey[500],
                  border: `1px solid ${theme.palette.grey[300]}`,
                  transform: 'none',
                  boxShadow: 'none'
                }
              }
            }
          }}
        />
      </Box>
    </Paper>

    {/* User Actions Menu */}
    <Menu
      anchorEl={userMenuAnchor}
      open={Boolean(userMenuAnchor)}
      onClose={() => setUserMenuAnchor(null)}
      disableScrollLock
    >
      {canEditUser(selectedUser) && (
        <MenuItem
          onClick={() => {
            setEditUserForm({
              id: selectedUser.id,
              role_id: selectedUser.roles.id,
              status: selectedUser.status
            });
            setEditUserDialogOpen(true);
            setUserMenuAnchor(null);
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
      )}
      {canDeactivateUser(selectedUser) && selectedUser.status === 'active' && (
        <MenuItem
          onClick={async () => {
            try {
              const { error } = await supabase
                .from('company_users')
                .update({ status: 'inactive' })
                .eq('id', selectedUser.id);

            if (error) throw error;

            setUserMenuAnchor(null);
            await loadUsers();
          } catch (error) {
            console.error('Error deactivating user:', error);
          }
        }}
        sx={{ color: 'warning.main' }}
      >
        <DeactivateIcon sx={{ mr: 1 }} />
        Deactivate User
      </MenuItem>
    )}
      {canReactivateUser(selectedUser) && selectedUser.status === 'inactive' && (
        <MenuItem
          onClick={async () => {
            try {
              const { error } = await supabase
                .from('company_users')
                .update({ status: 'active' })
                .eq('id', selectedUser.id);

              if (error) throw error;

              setUserMenuAnchor(null);
              await loadUsers();
            } catch (error) {
              console.error('Error reactivating user:', error);
            }
          }}
          sx={{ color: 'success.main' }}
        >
          <ReactivateIcon sx={{ mr: 1 }} />
          {selectedUser?.roles?.hierarchy_level <= 2 ? 'Reactivate Admin' : 'Reactivate User'}
        </MenuItem>
      )}

      {canCompletelyRemoveUser(selectedUser) && (
        <MenuItem
          onClick={() => handleCompleteRemovalClick(selectedUser)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Complete Removal
        </MenuItem>
      )}
    </Menu>

    {/* Edit User Dialog */}
    <Dialog 
      open={editUserDialogOpen} 
      onClose={() => setEditUserDialogOpen(false)} 
      maxWidth="sm" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500,
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: 'primary.main'
      }}>
        Edit User
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Box>
            <Typography variant="body2" sx={{ 
              mb: 1, 
              fontWeight: 500, 
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}>
              Role
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={openRoleSelectionDialog}
              sx={{
                height: 56,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: alpha(theme.palette.grey[50], 0.8),
                color: 'text.primary',
                fontWeight: 500,
                textTransform: 'none',
                justifyContent: 'flex-start',
                px: 2,
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                {editUserForm.role_id ? (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {roles.find(r => r.id === editUserForm.role_id)?.display_name || 'Unknown Role'} (Level {roles.find(r => r.id === editUserForm.role_id)?.hierarchy_level || 'N/A'})
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a role...
                  </Typography>
                )}
              </Box>
            </Button>
          </Box>
          
          <FormControl fullWidth>
            <InputLabel sx={{ fontWeight: 500 }}>Status</InputLabel>
            <Select
              value={editUserForm.status}
              onChange={(e) => setEditUserForm({ ...editUserForm, status: e.target.value })}
              label="Status"
              sx={{
                borderRadius: 2,
                background: alpha(theme.palette.grey[50], 0.8),
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5)
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px'
                }
              }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={() => setEditUserDialogOpen(false)} 
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdateUser}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            color: 'text.secondary',
            fontWeight: 500,
            px: 4,
            py: 1.2,
            '&:hover': { 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
              transform: 'translateY(-1px)'
            }
          }}
        >
          Update User
        </Button>
      </DialogActions>
    </Dialog>

    {/* Confirm Complete Removal Dialog */}
    <Dialog 
      open={confirmCompleteRemovalDialogOpen} 
      onClose={() => setConfirmCompleteRemovalDialogOpen(false)}
      maxWidth="sm" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500, 
        color: 'error.main',
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
      }}>
        Complete Removal
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Typography sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 500 }}>
          Are you sure you want to <strong>completely remove</strong> <strong>{userToCompletelyRemove?.profile?.full_name || userToCompletelyRemove?.email}</strong> from the company?
        </Typography>
        <Box sx={{
          p: 3,
          borderRadius: 2,
          background: alpha(theme.palette.error.main, 0.05),
          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            This action will <strong>permanently remove</strong> them from the company regardless of their role level. They will lose all access immediately and will need to be <strong>re-invited</strong> to regain access. This cannot be undone.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={() => setConfirmCompleteRemovalDialogOpen(false)}
          variant="outlined"
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            borderColor: alpha(theme.palette.grey[400], 0.8),
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1),
              borderColor: alpha(theme.palette.grey[500], 0.8)
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="error"
          onClick={handleConfirmCompleteRemoval}
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 4,
            py: 1.2,
            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
            }
          }}
        >
          Complete Removal
        </Button>
      </DialogActions>
    </Dialog>

    {/* Role Selection Dialog */}
    <Dialog 
      open={roleSelectionDialogOpen} 
      onClose={closeRoleSelectionDialog} 
      maxWidth="md" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)',
          minHeight: '70vh',
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500,
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: 'primary.main'
      }}>
        Select Role
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          {/* Search and Filter Section */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`
          }}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Search Roles
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Search by name, display name, or description..."
                    value={roleSelectionSearchTerm}
                    onChange={(e) => setRoleSelectionSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: 48,
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        py: 1.5,
                        px: 1.5
                      },
                      '& .MuiOutlinedInput-root': {
                        height: 48,
                        '& fieldset': { border: 'none' },
                        '&:hover fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none' }
                      }
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Hierarchy Level
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={roleSelectionHierarchyFilter}
                      onChange={(e) => setRoleSelectionHierarchyFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: 48,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' }
                      }}
                    >
                      <MenuItem value="">
                        <Typography sx={{ color: 'text.secondary' }}>All Levels</Typography>
                      </MenuItem>
                      {[...new Set(roles.map(role => role.hierarchy_level))]
                        .sort((a, b) => a - b)
                        .filter(level => level > 1)
                        .map(level => (
                          <MenuItem key={level} value={level.toString()}>Level {level}</MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Roles List */}
          <Box sx={{ pr: 1 }}>
            {Object.entries(getFilteredRolesForSelection())
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([level, levelRoles]) => (
              <Box key={level} sx={{ mb: 3 }}>
                {/* Hierarchy Level Header */}
                <Paper sx={{ 
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.2)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={`Level ${level}`}
                      color={getRoleStatusColor(parseInt(level))}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      {getHierarchyLevelName(parseInt(level))}
                    </Typography>
                    <Chip
                      label={`${levelRoles.length} ${levelRoles.length === 1 ? 'Role' : 'Roles'}`}
                      size="small"
                      sx={{ 
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: 'info.main',
                        fontWeight: 500
                      }}
                    />
                  </Box>
                </Paper>

                {/* Roles in this level */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {levelRoles.map((role) => (
                    <Paper
                      key={role.id}
                      onClick={() => handleRoleSelection(role)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: selectedRoleForDialog?.id === role.id 
                          ? `2px solid ${theme.palette.primary.main}` 
                          : `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: selectedRoleForDialog?.id === role.id 
                          ? alpha(theme.palette.primary.main, 0.08) 
                          : 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${theme.palette.primary.main}`
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                              {role.display_name}
                            </Typography>
                            {role.is_system_role && (
                              <Chip
                                label="System"
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(theme.palette.grey[600], 0.1),
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            )}
                          </Box>
                          {role.description && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              {role.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={closeRoleSelectionDialog} 
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirmRoleSelection}
          disabled={!selectedRoleForDialog}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            color: 'text.secondary',
            fontWeight: 500,
            px: 4,
            py: 1.2,
            '&:hover': { 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              backgroundColor: 'grey.400',
              color: 'grey.600'
            }
          }}
        >
          Select Role
        </Button>
      </DialogActions>
    </Dialog>
    </Box>
  );
}